import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, FILE_URL, formatBRL, displayBrand } from "../lib/api";

const LOADING_MESSAGES = [
  "Encontrando as peças perfeitas para você...",
  "Nossa curadoria está sendo preparada...",
  "Quase lá...",
];

const STEPS = [
  {
    key: "style",
    title: "Quais dessas palavras te descrevem melhor?",
    options: [
      { value: "Clássico", label: "Clássica", subtitle: "Elegância atemporal" },
      { value: "Moderno", label: "Moderna", subtitle: "Design contemporâneo" },
      { value: "Minimalista", label: "Minimalista", subtitle: "Menos é mais" },
      { value: "Romântico", label: "Romântica", subtitle: "Detalhes delicados" },
      { value: "Ousado", label: "Ousada", subtitle: "Personalidade forte" },
      { value: "Boho", label: "Boho", subtitle: "Orgânico e livre" },
    ],
    multi: true,
    maxMulti: 2,
  },
  {
    key: "for_whom",
    title: "Para ela, para ele ou unissex?",
    // DB values are lowercase ("Para ela", "Para ele", "Unissex") — match them exactly.
    options: [
      { value: "Para ela", label: "Para ela" },
      { value: "Para ele", label: "Para ele" },
      { value: "Unissex", label: "Unissex" },
    ],
    multi: false,
  },
  {
    key: "combines_with",
    title: "Quais tipos de look você quer compor?",
    // Multi-select — usuário pode escolher mais de um look (ex: Casual + Executivo).
    // DB stores lowercase phrasing; mapping uses bulk_import.COMBINES_MAP values.
    options: [
      { value: "Look casual",      label: "Look Casual",      subtitle: "Para o dia a dia" },
      { value: "Look executivo",   label: "Look Executivo",   subtitle: "No trabalho e reuniões" },
      { value: "Vestido de festa", label: "Vestido de Festa", subtitle: "Para brilhar à noite" },
      { value: "Noiva",            label: "Noivado",          subtitle: "Para um sim eterno" },
      { value: "Praia/verão",      label: "Praia/Verão",      subtitle: "Leveza e sol" },
      { value: "All black",        label: "All Black",        subtitle: "Sobriedade impecável" },
    ],
    multi: true,
    maxMulti: 6,
  },
  {
    key: "occasion",
    title: "Em quais lugares você pretende usar?",
    // Multi-select — peças podem servir várias ocasiões.
    options: [
      { value: "Dia a Dia", label: "Dia a Dia", subtitle: "No trabalho, no café, na vida" },
      { value: "Casamento", label: "Casamento", subtitle: "Para o sim e além" },
      { value: "Noivado", label: "Noivado", subtitle: "Símbolo de um começo" },
      { value: "Ocasião Especial", label: "Ocasião Especial", subtitle: "Datas que merecem brilho" },
    ],
    multi: true,
    maxMulti: 4,
  },
  {
    key: "sophistication",
    title: "Você prefere joias que...",
    slider: true,
  },
];

export default function StyleQuizPage() {
  const [stepIdx, setStepIdx] = useState(0);
  // M220 — pre-seed sophistication=3 (the slider's default visual position) so
  // a visitor who reaches step 5 and doesn't drag the slider can still hit
  // "Ver minha curadoria". Before this, the slider visually showed "3" but
  // `answers.sophistication` stayed undefined, leaving the CTA disabled and
  // making "3" feel like the only un-selectable value.
  const [answers, setAnswers] = useState({ sophistication: 3 });
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState(0);
  const [result, setResult] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!loading) return;
    const t = setInterval(() => setLoadingMsg((i) => (i + 1) % LOADING_MESSAGES.length), 1800);
    return () => clearInterval(t);
  }, [loading]);

  const current = STEPS[stepIdx];
  const isLast = stepIdx === STEPS.length - 1;

  const select = (value) => {
    if (current.multi) {
      setAnswers((prev) => {
        const cur = prev[current.key] || [];
        if (cur.includes(value)) return { ...prev, [current.key]: cur.filter((v) => v !== value) };
        if (cur.length >= (current.maxMulti || 2)) return prev;
        return { ...prev, [current.key]: [...cur, value] };
      });
    } else {
      setAnswers((prev) => ({ ...prev, [current.key]: value }));
    }
  };

  const next = async () => {
    if (!isLast) { setStepIdx(stepIdx + 1); return; }
    setLoading(true);
    try {
      const payload = {
        style: answers.style || [],
        for_whom: answers.for_whom || null,
        combines_with: answers.combines_with || [],
        occasion: answers.occasion || [],
        sophistication: answers.sophistication ? Number(answers.sophistication) : null,
        price_range: "any",
      };
      // ensure at least 2s loading for perceived value
      const [res] = await Promise.all([
        api.post("/style-match", payload),
        new Promise((r) => setTimeout(r, 2200)),
      ]);
      // M219: backend already round-robins categories inside the matched
      // candidate pool, so we just consume the response as-is. `has_more`
      // controls whether we keep the "Mais Recomendações" CTA visible.
      setResult(res.data);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  // M219 — appends 8 more profile-matched pieces below the current grid,
  // excluding ids already shown. Backend signals `has_more=false` when the
  // catalogue is exhausted for this profile; we hide the CTA at that point.
  const loadMore = async () => {
    if (!result || !result.has_more || loadingMore) return;
    setLoadingMore(true);
    try {
      const seen = result.products.map((p) => p.id);
      const payload = {
        style: answers.style || [],
        for_whom: answers.for_whom || null,
        combines_with: answers.combines_with || [],
        occasion: answers.occasion || [],
        sophistication: answers.sophistication ? Number(answers.sophistication) : null,
        price_range: "any",
        exclude_ids: seen,
      };
      const r = await api.post("/style-match", payload);
      const fresh = (r.data?.products || []).filter((p) => !seen.includes(p.id));
      setResult((prev) => ({
        ...prev,
        products: [...prev.products, ...fresh],
        has_more: !!r.data?.has_more && fresh.length > 0,
      }));
    } catch {
      // keep the current grid intact on error
    } finally {
      setLoadingMore(false);
    }
  };

  const back = () => {
    if (stepIdx > 0) setStepIdx(stepIdx - 1);
  };

  const isAnswered = () => {
    const a = answers[current.key];
    if (current.slider) return a !== undefined;
    if (current.multi) return Array.isArray(a) && a.length > 0;
    return !!a;
  };

  if (loading) {
    return (
      <div className="bg-[#FAF9F7] min-h-screen flex flex-col items-center justify-center p-6" data-testid="quiz-loading">
        <div className="w-3 h-3 bg-[#B8A88A] rounded-full pulse-accent mb-8" />
        <p className="font-italic-display text-2xl md:text-3xl text-[#6B6B6B] text-center max-w-md">
          {LOADING_MESSAGES[loadingMsg]}
        </p>
      </div>
    );
  }

  if (result) {
    return (
      <div className="bg-white min-h-screen py-12" data-testid="quiz-result">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="overline text-[#B8A88A] mb-4">Sua curadoria</p>
            <h1 className="font-display text-4xl md:text-5xl mb-6 leading-tight">
              Selecionamos {result.products.length} peças para você
            </h1>
            <p className="font-italic-display text-lg md:text-xl text-[#6B6B6B] leading-relaxed">
              {result.paragraph}
            </p>
          </div>

          {result.products.length === 0 ? (
            <div className="text-center">
              <p className="text-[#6B6B6B] mb-8">Não encontramos peças que combinem exatamente com seu perfil agora.</p>
              <Link to="/joias/todos" className="btn-primary">Explorar todas as peças</Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
              {result.products.map((p) => {
                const img = p.images?.[0] ? FILE_URL(p.images[0]) : "https://images.unsplash.com/photo-1611652022419-a9419f74343d?w=600&q=80";
                return (
                  <Link key={p.id} to={`/produto/${p.id}`} className="group" data-testid={`quiz-result-${p.id}`}>
                    <div className="aspect-[3/4] bg-[#FAF9F7] overflow-hidden mb-3">
                      <img src={img} alt={p.name} className="w-full h-full object-cover img-hover" />
                    </div>
                    {(() => { const b = displayBrand(p); return b ? <p className="text-[10px] uppercase tracking-wider text-[#A8A8A8]">{b}</p> : null; })()}
                    <p className="font-display text-base mb-1">{p.name}</p>
                    <p className="text-sm">{formatBRL(p.price)}</p>
                  </Link>
                );
              })}
            </div>
          )}

          <div className="text-center mt-16 flex flex-wrap gap-3 justify-center">
            {result.has_more ? (
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="btn-accent disabled:opacity-40"
                data-testid="quiz-load-more"
              >
                {loadingMore ? "Carregando…" : "Mais Recomendações"}
              </button>
            ) : (
              result.products.length >= 8 && (
                <p className="w-full text-sm italic text-[#A8A8A8] mb-2" data-testid="quiz-no-more-msg">
                  Você já viu todas as peças do seu perfil.
                </p>
              )
            )}
            <button onClick={() => { setResult(null); setStepIdx(0); setAnswers({ sophistication: 3 }); }} className="btn-secondary" data-testid="quiz-redo">
              Refazer quiz
            </button>
            <Link to="/joias/todos" className="btn-primary">Ver todas as peças</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#FAF9F7] min-h-screen py-12" data-testid="quiz-page">
      <div className="max-w-3xl mx-auto px-6">
        <div className="flex items-center justify-between mb-6">
          <Link to="/try-on" className="overline text-[#6B6B6B] hover:text-[#B8A88A] inline-flex items-center gap-2">
            <ArrowLeft size={14}/> Voltar
          </Link>
          <span className="overline text-[#A8A8A8]">
            {stepIdx + 1} / {STEPS.length}
          </span>
        </div>

        <div className="h-[2px] bg-[#E8E4DF] mb-12">
          <div className="h-full bg-[#B8A88A] transition-all duration-500" style={{ width: `${((stepIdx + 1) / STEPS.length) * 100}%` }} />
        </div>

        <h2 className="font-display text-3xl md:text-5xl text-center mb-3 leading-tight">{current.title}</h2>
        {current.multi && (
          <p className="text-center text-xs text-[#A8A8A8] italic mb-10" data-testid="quiz-multi-hint">
            Você pode escolher mais de uma opção
          </p>
        )}
        {!current.multi && <div className="mb-10" />}

        {current.slider ? (
          <div className="px-4 mb-16">
            <input
              type="range"
              min="1"
              max="5"
              step="1"
              value={answers.sophistication || 3}
              onChange={(e) => setAnswers({ ...answers, sophistication: e.target.value })}
              className="w-full accent-[#B8A88A]"
              data-testid="sophistication-slider"
            />
            <div className="flex justify-between text-xs text-[#6B6B6B] mt-4">
              <div className="text-left max-w-[40%]">
                <p className="font-medium text-[#1A1A1A]">Delicadas e sutis</p>
                <p>complementam o look</p>
              </div>
              <div className="text-right max-w-[40%]">
                <p className="font-medium text-[#1A1A1A]">Marcantes e Statement</p>
                <p>são o centro das atenções</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-12">
            {current.options.map((opt) => {
              const isSelected = current.multi
                ? (answers[current.key] || []).includes(opt.value)
                : answers[current.key] === opt.value;
              return (
                <button
                  key={opt.value}
                  data-testid={`quiz-option-${opt.value}`}
                  onClick={() => select(opt.value)}
                  className={`p-5 md:p-6 bg-white border text-left transition-all ${
                    isSelected
                      ? "border-[#B8A88A] ring-1 ring-[#B8A88A] scale-[1.02]"
                      : "border-[#E8E4DF] hover:border-[#B8A88A]"
                  }`}
                >
                  <p className="font-display text-lg md:text-xl">{opt.label}</p>
                  {opt.subtitle && <p className="text-xs text-[#6B6B6B] mt-1">{opt.subtitle}</p>}
                </button>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-between">
          {stepIdx > 0 ? (
            <button onClick={back} className="text-sm text-[#6B6B6B] hover:text-[#1A1A1A]" data-testid="quiz-back">
              ← Anterior
            </button>
          ) : <span />}
          <button
            data-testid="quiz-next"
            onClick={next}
            disabled={!isAnswered()}
            className="btn-accent disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isLast ? "Ver minha curadoria" : "Próxima"}
          </button>
        </div>
      </div>
    </div>
  );
}
